// src/components/public_utilities/pu/PuAssetLayer.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities asset layer for GisMapView
// --- UPDATE (Public Utilities v3 — PU-only map) — NEW FILE ---------------------------------------
// Draws the Public Utilities assets (from GET /public-utilities/map — no roads, no 0,0 points)
// fast enough for the full New Providence inventory (1,800+ wells and catch basins, room for
// tens of thousands):
//   • points are drawn on ONE canvas (L.canvas) instead of 1,800 DOM markers
//   • below zoom 15 nearby points are grouped into counted clusters (grid clustering in screen
//     pixels, recomputed on zoom / pan); a red ring means at least one asset in the cluster is
//     overdue for inspection, failed or damaged; click a cluster to zoom into it
//   • colour by asset type, status, next-inspection state or who manages it (puMapStyles)
//   • constituency (= zone) polygons in their own pane under the points, with asset counts
//   • the selected asset gets a halo; clicking a point calls onSelect(feature)
// It only adds its own layers/panes and removes them on unmount — the rest of the map is untouched.
// --- END UPDATE ------------------------------------------------------------------------------
import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { puColorFor, managerColorIndex, needsAttention, PU_ZONE_STYLE, ZONE_COLOR_HEX, TYPE_COLORS } from './puMapStyles';

export const CLUSTER_OFF_ZOOM = 15;
export const CLUSTER_MIN_POINTS = 250;
const CELL_PX = 56;

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const humanize = (s) => String(s || '').replace(/_/g, ' ');

/**
 * Pure grid clustering (exported for tests).
 * @param {Array<{lat:number,lng:number}>} points
 * @param {(p) => {x:number,y:number}} project   latlng → screen pixels at the current zoom
 * @param {number} cell                           cell size in px
 * @returns {Array<{ lat, lng, items: object[] }>}
 */
export function clusterPoints(points, project, cell = CELL_PX) {
    const cells = new Map();
    for (const p of points) {
        const px = project(p);
        const key = `${Math.floor(px.x / cell)}:${Math.floor(px.y / cell)}`;
        let c = cells.get(key);
        if (!c) { c = { sumLat: 0, sumLng: 0, items: [] }; cells.set(key, c); }
        c.sumLat += p.lat; c.sumLng += p.lng; c.items.push(p);
    }
    return [...cells.values()].map((c) => ({ lat: c.sumLat / c.items.length, lng: c.sumLng / c.items.length, items: c.items }));
}

function dominant(items, keyFn) {
    const n = new Map(); let best = null; let bestN = 0;
    for (const it of items) { const k = keyFn(it); const v = (n.get(k) || 0) + 1; n.set(k, v); if (v > bestN) { bestN = v; best = k; } }
    return { key: best, counts: n };
}

const tooltipHtml = (f) => `<div style="font-size:12px;line-height:1.35">
  <div style="font-weight:800">${esc(f.code || '')}${f.code ? ' · ' : ''}${esc(f.name || humanize(f.type))}</div>
  <div style="opacity:.8">${esc(humanize(f.type))} · ${esc(humanize(f.status))}</div>
  ${f.constituency ? `<div style="opacity:.8">${esc(f.constituency)}</div>` : ''}
  ${f.next_due ? `<div style="opacity:.8">Next inspection ${esc(f.next_due)}${f.due === 'overdue' ? ' — <b style="color:#E53E3E">overdue</b>' : ''}</div>` : '<div style="opacity:.6">No inspection scheduled</div>'}
</div>`;

function PuAssetLayer({
    features = [], zones = [], managers = [], colorBy = 'type', visibleTypes = null, showZones = true,
    selectedId = null, onSelect, fitOnLoad = true, clusterOffZoom = CLUSTER_OFF_ZOOM, clusterMinPoints = CLUSTER_MIN_POINTS,
}) {
    const map = useMap();
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;
    const stateRef = useRef({ renderer: null, points: null, clusters: null, halo: null, zones: null, fitted: false, raf: 0 });

    const managerColors = useMemo(() => managerColorIndex(managers), [managers]);
    const visible = useMemo(() => {
        const vt = visibleTypes instanceof Set ? visibleTypes : Array.isArray(visibleTypes) ? new Set(visibleTypes) : null;
        return vt && vt.size ? features.filter((f) => vt.has(f.type)) : features;
    }, [features, visibleTypes]);
    const byId = useMemo(() => new Map(features.map((f) => [String(f.id), f])), [features]);

    // panes + groups (once)
    useEffect(() => {
        const st = stateRef.current;
        if (!map.getPane('pu-zones')) { const p = map.createPane('pu-zones'); p.style.zIndex = 350; }
        if (!map.getPane('pu-assets')) { const p = map.createPane('pu-assets'); p.style.zIndex = 450; }
        st.renderer = L.canvas({ padding: 0.4, pane: 'pu-assets' });
        st.points = L.layerGroup().addTo(map);
        st.clusters = L.layerGroup().addTo(map);
        st.halo = L.layerGroup().addTo(map);
        st.zones = L.layerGroup().addTo(map);
        return () => {
            cancelAnimationFrame(st.raf);
            [st.points, st.clusters, st.halo, st.zones].forEach((g) => { try { g.remove(); } catch (_) { /* map gone */ } });
            st.points = st.clusters = st.halo = st.zones = null;
        };
    }, [map]);

    // zones (constituencies)
    useEffect(() => {
        const g = stateRef.current.zones;
        if (!g) return;
        g.clearLayers();
        if (!showZones) return;
        for (const z of zones) {
            if (!z.geometry) continue;
            const color = ZONE_COLOR_HEX[z.color] || z.color || PU_ZONE_STYLE.color;
            try {
                L.geoJSON(z.geometry, { pane: 'pu-zones', interactive: true, style: () => ({ ...PU_ZONE_STYLE, color, fillColor: color }) })
                    .bindTooltip(`<b>${esc(z.constituency || z.name)}</b><br/>${z.assets ?? 0} Public Utilities assets`, { sticky: true, direction: 'top' })
                    .addTo(g);
            } catch (_) { /* bad polygon — skip it, never break the map */ }
        }
    }, [zones, showZones]);

    // points / clusters
    useEffect(() => {
        const st = stateRef.current;
        if (!st.points) return undefined;

        const draw = () => {
            st.points.clearLayers(); st.clusters.clearLayers();
            const zoom = map.getZoom();
            const bounds = map.getBounds().pad(0.25);
            const inView = visible.filter((f) => bounds.contains([f.lat, f.lng]));
            const cluster = zoom < clusterOffZoom && inView.length > clusterMinPoints;
            if (!cluster) {
                for (const f of inView) {
                    const flag = needsAttention(f);
                    L.circleMarker([f.lat, f.lng], {
                        renderer: st.renderer, radius: zoom >= 17 ? 8 : 6, weight: flag ? 2.5 : 1.25,
                        color: flag ? '#E53E3E' : '#FFFFFF', fillColor: puColorFor(f, colorBy, managerColors), fillOpacity: 0.95,
                    })
                        .bindTooltip(() => tooltipHtml(f), { direction: 'top', offset: [0, -6] })
                        .on('click', () => onSelectRef.current?.(f))
                        .addTo(st.points);
                }
                return;
            }
            const groups = clusterPoints(inView, (p) => map.project([p.lat, p.lng], zoom));
            for (const c of groups) {
                if (c.items.length === 1) {
                    const f = c.items[0];
                    L.circleMarker([f.lat, f.lng], { renderer: st.renderer, radius: 5, weight: 1, color: needsAttention(f) ? '#E53E3E' : '#FFFFFF', fillColor: puColorFor(f, colorBy, managerColors), fillOpacity: 0.95 })
                        .bindTooltip(() => tooltipHtml(f), { direction: 'top' })
                        .on('click', () => onSelectRef.current?.(f))
                        .addTo(st.points);
                    continue;
                }
                const n = c.items.length;
                const dom = dominant(c.items, (f) => puColorFor(f, colorBy, managerColors));
                const flagged = c.items.filter(needsAttention).length;
                const size = Math.round(26 + Math.min(26, Math.log2(n) * 4));
                const icon = L.divIcon({
                    className: 'pu-cluster-icon',
                    iconSize: [size, size],
                    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;
                        background:${dom.key};color:#0A1020;font:800 ${n >= 1000 ? 10 : 11}px/1 system-ui,sans-serif;
                        box-shadow:0 0 0 ${flagged ? 3 : 2}px ${flagged ? '#E53E3E' : 'rgba(255,255,255,.85)'},0 2px 8px rgba(0,0,0,.45)" aria-label="${n} assets">${n}</div>`,
                });
                const types = dominant(c.items, (f) => f.type).counts;
                const typeText = [...types.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([t, k]) => `${k} ${esc(humanize(t))}`).join(' · ');
                const latlngs = c.items.map((f) => [f.lat, f.lng]);
                L.marker([c.lat, c.lng], { icon, keyboard: true, title: `${n} assets` })
                    .bindTooltip(`<b>${n} assets</b><br/>${typeText}${flagged ? `<br/><span style="color:#E53E3E;font-weight:700">${flagged} need attention</span>` : ''}<br/><span style="opacity:.7">Click to zoom in</span>`, { direction: 'top' })
                    .on('click', () => { try { map.fitBounds(L.latLngBounds(latlngs), { padding: [40, 40], maxZoom: clusterOffZoom + 1 }); } catch (_) { map.setView([c.lat, c.lng], zoom + 2); } })
                    .addTo(st.clusters);
            }
        };
        const schedule = () => { cancelAnimationFrame(st.raf); st.raf = requestAnimationFrame(draw); };
        draw();
        map.on('zoomend moveend', schedule);
        return () => { map.off('zoomend moveend', schedule); cancelAnimationFrame(st.raf); };
    }, [map, visible, colorBy, managerColors, clusterOffZoom, clusterMinPoints]);

    // halo for the selected asset
    useEffect(() => {
        const g = stateRef.current.halo;
        if (!g) return;
        g.clearLayers();
        const f = selectedId != null ? byId.get(String(selectedId)) : null;
        if (!f) return;
        L.circleMarker([f.lat, f.lng], { radius: 13, weight: 3, color: '#00D4AA', fill: false, interactive: false, pane: 'pu-assets' }).addTo(g);
    }, [selectedId, byId]);

    // fit once to the inventory
    useEffect(() => {
        const st = stateRef.current;
        if (!fitOnLoad || st.fitted || !features.length) return;
        st.fitted = true;
        try { map.fitBounds(L.latLngBounds(features.map((f) => [f.lat, f.lng])), { padding: [30, 30], maxZoom: 15 }); } catch (_) { /* ignore */ }
    }, [features, fitOnLoad, map]);

    return null;
}

export { TYPE_COLORS };
export default PuAssetLayer;
